import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Upload, Trash2, FolderOpen, Image as ImageIcon, Music, Video, FileText, Loader2, X, Sparkles, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Source" tabs
const KIND_TABS = [
  { key: 'all',       label: 'All',        icon: FolderOpen },
  { key: 'image',     label: 'Images',     icon: ImageIcon,   ext: '.png,.jpg,.jpeg,.webp,.gif,.svg' },
  { key: 'audio',     label: 'Audio',      icon: Music,       ext: '.mp3,.wav,.ogg,.m4a' },
  { key: 'video',     label: 'Video',      icon: Video,       ext: '.mp4,.webm' },
  { key: 'html',      label: 'Games',      icon: FileText,    ext: '.html,.htm' },
  { key: 'data',      label: 'Data',       icon: FileText,    ext: '.json,.txt,.glb,.gltf,.fbx' },
  { key: 'generated', label: 'AI',         icon: Sparkles },
  { key: 'imported',  label: 'Marketplace',icon: ShoppingBag },
];

function normalizeAsset(a) {
  // Marketplace-imported or AI-generated game_asset → Library card shape.
  const isImport = !!a.imported_from;
  return {
    id: a.asset_id,
    source: isImport ? 'imported' : 'generated',
    filename: a.name || a.prompt?.slice(0, 60) || 'asset',
    url: a.url,
    kind: 'image', // treat sprites/textures/models as images for preview
    asset_type: a.asset_type || 'sprite',
    size_bytes: 0,
    prompt: a.prompt,
    created_at: a.created_at,
    _raw: a,
  };
}
function normalizeUpload(u) {
  return {
    id: u.upload_id,
    source: 'upload',
    filename: u.filename || 'upload',
    url: u.url,
    kind: u.kind || 'image',
    // Use 'auto' so the editor's dropAssetOnScene classifier picks based on aspect ratio (wide → background, else sprite).
    asset_type: u.kind === 'image' ? 'auto' : 'sprite',
    size_bytes: u.size_bytes || 0,
    upload_kind: u.kind,
    created_at: u.created_at,
    _raw: u,
  };
}

export default function Library() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState('all');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // asset for full-screen preview
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [uploadsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/uploads/mine?kind=all`, { withCredentials: true }).catch(() => ({ data: { uploads: [] } })),
        axios.get(`${API}/assets/mine`, { withCredentials: true }).catch(() => ({ data: { assets: [] } })),
      ]);
      const merged = [
        ...(uploadsRes.data?.uploads || []).map(normalizeUpload),
        ...(assetsRes.data?.assets || []).map(normalizeAsset),
      ];
      merged.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setItems(merged);
    } catch { toast.error('Failed to load library'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = items.filter(it => {
    if (kind === 'all') return true;
    if (kind === 'generated') return it.source === 'generated';
    if (kind === 'imported') return it.source === 'imported';
    return it.source === 'upload' && it.kind === kind;
  });

  const uploadFile = async (file) => {
    if (!file) return false;
    const fd = new FormData();
    fd.append('file', file);
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const kindFromExt =
      ['png','jpg','jpeg','webp','gif','svg'].includes(ext) ? 'image' :
      ['mp3','wav','ogg','m4a'].includes(ext) ? 'audio' :
      ['mp4','webm'].includes(ext) ? 'video' :
      ['html','htm'].includes(ext) ? 'html' :
      ['json','txt','glb','gltf','fbx'].includes(ext) ? 'data' :
      (['image','audio','video','html','data'].includes(kind) ? kind : 'image');
    fd.append('kind', kindFromExt);
    try {
      const r = await axios.post(`${API}/uploads`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setItems(prev => [normalizeUpload(r.data.upload), ...prev]);
      return true;
    } catch (e) {
      toast.error(`${file.name}: ${e.response?.data?.detail || 'Upload failed'}`);
      return false;
    }
  };

  const uploadMany = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      const success = await uploadFile(f); // eslint-disable-line no-await-in-loop
      if (success) ok += 1; else fail += 1;
    }
    setUploading(false);
    if (fail === 0) toast.success(`Uploaded ${ok} file${ok === 1 ? '' : 's'}`);
    else toast.warning(`${ok} uploaded, ${fail} failed`);
  };

  const remove = async (it) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      if (it.source === 'upload') {
        await axios.delete(`${API}/uploads/${it.id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API}/assets/${it.id}`, { withCredentials: true });
      }
      setItems(prev => prev.filter(u => u.id !== it.id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div
      className={`min-h-screen bg-[#050505] text-white overflow-x-hidden ${dragOver ? 'ring-2 ring-[#06B6D4] ring-inset' : ''}`}
      data-testid="library-page"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length > 0) uploadMany(files);
      }}
    >
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex-1" />
          <div className="text-sm text-zinc-400">
            <FolderOpen className="w-4 h-4 text-[#06B6D4] inline mr-1" />
            <span className="font-mono text-xs">{items.length} FILES</span>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-3 sm:px-6 pt-6 sm:pt-8 pb-4">
        <h1 className="text-2xl sm:text-3xl font-black mb-1">My Library</h1>
        <p className="text-xs sm:text-sm text-zinc-400 max-w-2xl">Uploads, AI generations and Marketplace imports live here. Drag any thumbnail into the Editor scene, tap to preview.</p>

        <div className="flex flex-wrap gap-2 mt-6 items-center overflow-x-auto -mx-1 px-1 pb-1">
          {KIND_TABS.map(k => (
            <button
              key={k.key}
              onClick={() => setKind(k.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-mono uppercase border transition-colors flex items-center gap-1.5 ${kind === k.key ? 'bg-[#06B6D4] text-black border-[#06B6D4]' : 'border-[#1E1E24] text-zinc-300 hover:text-white'}`}
              data-testid={`library-tab-${k.key}`}
            >
              <k.icon className="w-3 h-3" /> {k.label}
            </button>
          ))}
          <div className="flex-1" />
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => uploadMany(e.target.files)}
          />
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 shrink-0"
            data-testid="library-upload-btn"
            title="Upload files (or drag & drop anywhere on this page)"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Upload</>}
          </Button>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-6 pb-16">
        {loading ? (
          <div className="py-16 text-center text-zinc-500 text-sm">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-[#1E1E24] rounded-lg" data-testid="library-empty">
            <FolderOpen className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">
              {kind === 'imported' ? 'Nothing imported yet — browse the Marketplace to grab free assets.' :
               kind === 'generated' ? 'No AI generations yet — head to the Editor Assets panel to spawn some.' :
               'Nothing here yet. Pick a category above and upload something, or import from Marketplace.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="library-grid">
            {visible.map(u => (
              <div
                key={u.id}
                draggable
                onDragStart={(e) => {
                  const payload = {
                    asset_id: u.id,
                    url: u.url,
                    name: u.filename,
                    asset_type: u.asset_type || 'sprite',
                    upload_kind: u.upload_kind || u.kind,
                  };
                  e.dataTransfer.setData('application/x-playmold-asset', JSON.stringify(payload));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => setPreview(u)}
                className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg overflow-hidden group hover:border-[#06B6D4]/50 cursor-grab active:cursor-grabbing relative"
                title="Click to preview, drag onto Editor canvas to add to a scene"
                data-testid={`library-item-${u.id}`}
              >
                {/* Source badge */}
                <span className={`absolute top-2 left-2 z-10 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${
                  u.source === 'generated' ? 'bg-purple-500/80 text-white' :
                  u.source === 'imported' ? 'bg-amber-500/80 text-black' :
                  'bg-zinc-800/80 text-zinc-300'
                }`}>
                  {u.source === 'generated' ? 'AI' : u.source === 'imported' ? 'Market' : (u.kind || '').toUpperCase()}
                </span>

                <div className="aspect-square bg-[#121214] relative overflow-hidden flex items-center justify-center">
                  {(u.kind === 'image' || u.source !== 'upload') ? (
                    <img src={u.url} alt={u.filename} className="w-full h-full object-cover" loading="lazy" />
                  ) : u.kind === 'audio' ? (
                    <div className="w-full px-2"><Music className="w-8 h-8 mx-auto text-zinc-500 mb-1" /><audio src={u.url} controls className="w-full" onClick={(e) => e.stopPropagation()} /></div>
                  ) : u.kind === 'video' ? (
                    <video src={u.url} controls className="w-full h-full" onClick={(e) => e.stopPropagation()} />
                  ) : (
                    <FileText className="w-8 h-8 text-zinc-600" />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(u); }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-md bg-black/70 backdrop-blur text-white hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`library-delete-${u.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-2">
                  <p className="text-[11px] text-white truncate" title={u.filename}>{u.filename}</p>
                  <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500">
                    <span className="truncate">{(u.asset_type || u.kind || '').toUpperCase()}</span>
                    {u.size_bytes > 0 && <span className="shrink-0 ml-1">{Math.round(u.size_bytes / 1024)} KB</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Preview modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="library-preview-modal"
        >
          <button
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 border border-[#1E1E24] flex items-center justify-center text-white hover:text-[#06B6D4]"
            data-testid="library-preview-close"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-3xl w-full max-h-[85vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <div className="w-full flex-1 flex items-center justify-center overflow-hidden rounded-lg bg-[#0A0A0B] border border-[#1E1E24]">
              {(preview.kind === 'image' || preview.source !== 'upload') ? (
                <img src={preview.url} alt={preview.filename} className="max-w-full max-h-[70vh] object-contain" />
              ) : preview.kind === 'audio' ? (
                <div className="p-8 text-center"><Music className="w-16 h-16 mx-auto text-[#06B6D4] mb-3" /><audio src={preview.url} controls autoPlay /></div>
              ) : preview.kind === 'video' ? (
                <video src={preview.url} controls autoPlay className="max-w-full max-h-[70vh]" />
              ) : (
                <div className="p-8 text-center"><FileText className="w-16 h-16 mx-auto text-zinc-500 mb-3" /><a href={preview.url} target="_blank" rel="noreferrer" className="text-[#06B6D4] underline">Open file</a></div>
              )}
            </div>
            <div className="text-center">
              <p className="text-sm text-white font-medium">{preview.filename}</p>
              {preview.prompt && <p className="text-xs text-zinc-400 mt-1 max-w-md">{preview.prompt}</p>}
              <p className="text-[10px] font-mono text-zinc-600 mt-1">{preview.source.toUpperCase()} · {preview.asset_type || preview.kind}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
